/**
 * Projects that exist because somebody said so, and nothing else yet.
 *
 * A project in this app is not a record: it is a LABEL explorations carry, and
 * the groups down the sidebar are derived from the labels on the index rows.
 * That is the right model — it cannot go stale, it needs no migration, and a
 * group can never promise a membership the documents disagree with. It has
 * exactly one hole in it, and "+ New project" falls straight down it: a project
 * with no members is a label nothing carries, so there is nowhere for it to be.
 *
 * This is that nowhere, kept as small as it can be. A device-local list of
 * labels somebody has DECLARED and not yet filled, rendered as empty groups so
 * there is something to drag the first exploration into.
 *
 * THE RULE THAT KEEPS IT FROM BECOMING A SECOND SOURCE OF TRUTH: a label leaves
 * the moment a member arrives. From then on the group is derived like every
 * other, so the registry can never disagree with the documents about whether a
 * project exists — it only ever speaks about labels no document mentions.
 *
 * AND A DRAINED GROUP DOES NOT COME BACK HERE. Moving the last exploration out
 * of a project makes the group vanish, exactly as it did before any of this
 * existed. It would be easy to re-declare it on the way out and keep the heading
 * on screen, and that would be wrong: declared-then-abandoned and
 * drained-by-moves are the same state — a label nobody is using — and two ways
 * of being empty, one of which lingers and one of which does not, is a
 * distinction the user would have to learn in order to predict their own
 * sidebar. Emptying a project removes it. Making one is how you get it back.
 *
 * Pure, and it imports only the label clamp. The store owns the slot this list
 * is written to and is the only caller that touches storage.
 */

import { PROJECT_LABEL_MAX, projectLabel } from './types'

/**
 * How many empty projects one device may declare at a time.
 *
 * The list is written synchronously into the store's own table beside the keys,
 * where the whole point is that a write cannot be missed, so it has to stay
 * small enough to be cheap: forty labels at the codec's 60-character clamp is
 * about 2.5kB in the worst case. It is also well past what anybody is going to
 * declare before filling one — this is a bound on a runaway, not a budget
 * somebody is meant to feel.
 */
export const MAX_DECLARED = 40

/**
 * The declared list as it comes off disk — validated, clamped and deduped.
 *
 * Anything that is not an array of strings reads as none: this is bytes off a
 * shared origin like every other slot, and a hand-edited row must not open a
 * group no writer in this app could have produced. Every label comes through
 * `projectLabel`, the ONE clamp, so a declared group and a derived one with the
 * same name are the same group rather than two headings that never merge.
 */
export function parseDeclared(raw: string | null): string[] {
  if (!raw) return []
  let list: unknown
  try {
    list = JSON.parse(raw)
  } catch {
    return []
  }
  if (!Array.isArray(list)) return []
  const out: string[] = []
  for (const item of list) {
    const label = projectLabel(item)
    if (label && !out.includes(label)) out.push(label)
    if (out.length === MAX_DECLARED) break
  }
  return out
}

export function serialiseDeclared(declared: readonly string[]): string {
  return JSON.stringify(declared)
}

/**
 * Declare one label, newest first. Returns the list unchanged when there is
 * nothing to add — an empty name, or one already on it.
 *
 * NEWEST FIRST because that is the order the pane renders empty groups in, and
 * the order is the whole of what an empty group has going for it: it is a place
 * somebody just made and is about to drag into, so it earns the top until it has
 * a member and derivation starts sorting it by recency like everything else.
 *
 * The cap drops the OLDEST declaration rather than refusing the new one. A
 * refusal here would be a sentence about a limit nobody has been told about, at
 * the exact moment somebody is naming something; the label that falls off is the
 * one they declared furthest back and never filled.
 */
export function withDeclared(declared: readonly string[], raw: unknown): string[] {
  const label = projectLabel(raw)
  if (!label) return [...declared]
  const rest = declared.filter((d) => d !== label)
  return [label, ...rest].slice(0, MAX_DECLARED)
}

/** Take one label off the list. Clamped on the way in, so the ✕ on a heading and
 *  the name that made it are the same string. */
export function withoutDeclared(declared: readonly string[], raw: unknown): string[] {
  const label = projectLabel(raw)
  if (!label) return [...declared]
  return declared.filter((d) => d !== label)
}

/**
 * The list after the documents have had their say: every label something now
 * carries is derived, so it leaves.
 *
 * The one membership rule, in one place, so the store's writer and its tests are
 * looking at the same function. `inUse` is whatever the caller counts as a
 * member — the sidebar hands it every label on either shelf, because an
 * exploration in the archive is still an exploration in that project.
 */
export function declaredAfter(declared: readonly string[], inUse: ReadonlySet<string>): string[] {
  return declared.filter((d) => !inUse.has(d))
}

/** Whether the list would change. The store asks before it writes: a sync that
 *  set state on every pass would re-render the pane on every read of the index,
 *  and this one is called from a render effect. */
export function declaredChanged(a: readonly string[], b: readonly string[]): boolean {
  return a.length !== b.length || a.some((x, i) => x !== b[i])
}

/* ── Folded projects ──────────────────────────────────────────────────── */

/**
 * Projects whose rows are folded away under their heading (D101).
 *
 * A fold is TASTE about the list, so the list rides in `nv.prefs` with the
 * sidebar's own collapsed flag rather than in the store's table beside the
 * keys: the worst a lost write does is show a project's rows again. It is a
 * list of labels rather than a flag on anything, because a project is a label
 * and nothing else — there is no record to put a flag on — and a label that
 * is folded and then emptied, or renamed away, simply stops matching a
 * heading; nothing has to be pruned for the list to stay honest.
 */

/** A bound on a runaway, not a budget: nobody folds a hundred projects, and
 *  the list is written into a blob on every change. */
export const MAX_FOLDED = 100

/**
 * The folded labels as they come off the prefs blob — already-parsed JSON,
 * unlike the declared list's slot, so this takes the value and not the bytes.
 * Anything but an array reads as nothing folded, every label comes through the
 * one clamp, and each appears once.
 */
export function parseFolded(raw: unknown): string[] {
  if (!Array.isArray(raw)) return []
  const out: string[] = []
  for (const item of raw) {
    const label = projectLabel(item)
    if (label && !out.includes(label)) out.push(label)
    if (out.length === MAX_FOLDED) break
  }
  return out
}

/**
 * Fold a project, or unfold it — one door, because the folder on the heading
 * is one button. A new fold goes to the front, so the cap drops the oldest.
 * Null for a name with nothing in it, so the store writes nothing.
 */
export function toggleFolded(folded: readonly string[], raw: unknown): string[] | null {
  const label = projectLabel(raw)
  if (!label) return null
  if (folded.includes(label)) return folded.filter((f) => f !== label)
  return [label, ...folded].slice(0, MAX_FOLDED)
}

export { PROJECT_LABEL_MAX }
